import { useQuery } from "@apollo/client";
import { LOAD_VEHICLES_QUERY } from "globals/graphql";
import map from "lodash/map";
import { useMemo } from "react";

import { CreateTripState } from "pages/new/context/initialState";
import { formatStopsForBRACalculations } from "pages/new/utils/formatStopsForBRACalculations";
import { LoadVehiclesConnection, TripCategory } from "types";

type UseLoadVehiclesProps = {
  trip: CreateTripState;
  returnTrip: CreateTripState;
  tripCategory: TripCategory;
  ignoreVehicleCategory?: boolean;
};

function useLoadVehicles(props: UseLoadVehiclesProps) {
  const { trip, returnTrip, tripCategory, ignoreVehicleCategory = false } = props;

  const { vehicleStops, returnTripStops } = formatStopsForBRACalculations({
    trip,
    returnTrip,
  });

  // queries
  const { data: initialData, loading: isLoadingVehicles } = useQuery<{
    loadVehicles: LoadVehiclesConnection;
  }>(LOAD_VEHICLES_QUERY, {
    variables: {
      stops: vehicleStops,
      returnTripStops: returnTripStops,
      ignoreVehicleCategory,
    },
    fetchPolicy: "network-only", // set to ensure vehicles update for BRA cases
  });

  // second run, query for base rate
  const { data: braData, loading: isLoadingBRA } = useQuery<{
    loadVehicles: LoadVehiclesConnection;
  }>(LOAD_VEHICLES_QUERY, {
    skip: !initialData,
    variables: {
      tripType: tripCategory,
      stops: vehicleStops,
      returnTripStops: returnTripStops,
      ignoreVehicleCategory,
    },
    fetchPolicy: "network-only", // set to ensure vehicles update for BRA cases
  });

  // Use memoized vehicles that updates when either query completes
  // This is necessary as BRA data can reorder the vehicles,
  // e.g. if a vehicle is using tiered pricing as pricing is determined by the stops
  // and the stops are different in the BRA query
  const vehicles = useMemo(() => {
    // Prefer BRA data if available, otherwise use initial data
    const vehicleData = braData || initialData;
    return map(vehicleData?.loadVehicles.edges, "node") || [];
  }, [braData, initialData]);

  return {
    vehicles,
    isLoadingVehicles,
    isLoadingBRA,
  };
}

export default useLoadVehicles;
